import { useCallback, useMemo, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { usePageTitle } from '../../hooks/usePageTitle'
import { User } from '../../models/User'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { Icon } from '../Icon'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { SelectList } from '../common/SelectList'
import { SetupLocationPage } from '../settings/SetupLocationPage'
import { WorkflowPage } from '../workflows/WorkflowPage'

const isValidLocation = (user: User) => {
    return user.line1 && user.postalCode && user.city && user.state
}

const locationCityAndPostalCode = (user: User) => {
    if (!isValidLocation(user)) {
        null
    }
    return (
        <Paragraph
            size="sm"
            className="text-secondary-400"
        >{`${user.city}, ${user.postalCode}`}</Paragraph>
    )
}

export const SetupOnboardingLocationPage = () => {
    const { advanceOnboardingState, didCopyFromOtherAccount } = useOnboardingContext()
    const navigate = usePSNavigate()
    const [user, setUser] = useState<User>(PSApplication.me())
    const [selectedLocation, setSelectedLocation] = useState<
        (typeof locationListItems)[number] | undefined
    >()
    const [isEditingLocation, setIsEditingLocation] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    usePageTitle('Location')
    const { goBack } = useHistoryTrackerContext()

    const locationListItems = useMemo(() => {
        return [
            {
                id: 'vendor-location',
                title: 'At my location',
                subtitle: 'Clients will be shown my location when booking',
                thirdLine: locationCityAndPostalCode(user),
                tertiaryView: <Icon icon="home.large" background="round" />,
            },
            {
                id: 'client-location',
                title: 'Client selects location',
                subtitle: 'Clients will be asked to supply a location when booking',
                tertiaryView: <Icon icon="markerPin.large" background="round" />,
            },
            {
                id: 'virtual-location',
                title: 'Virtually',
                subtitle: 'Add a PocketSuite video conference link to appointments',
                tertiaryView: <Icon icon="monitor.large" background="round" />,
            },
            {
                id: 'mix-location',
                title: "It's a mix",
                subtitle: 'My appointments can be in-person, virtually, or a mix of both',
                tertiaryView: <Icon icon="globe2.large" background="round" />,
            },
        ]
    }, [user])

    const onNextHandler = useCallback(async () => {
        setIsSaving(true)
        await advanceOnboardingState()
        setIsSaving(false)
    }, [advanceOnboardingState])

    return (
        <>
            <WorkflowPage
                onClose={() => navigate('/')}
                onBack={() => goBack()}
                backButtonTitle={didCopyFromOtherAccount ? undefined : 'Back'}
                footerContent={
                    <div className="flex justify-end">
                        <Button
                            intent="primary"
                            type="submit"
                            disabled={isSaving || !selectedLocation}
                            isLoading={isSaving}
                            onClick={onNextHandler}
                        >
                            Next
                        </Button>
                    </div>
                }
            >
                <Heading size="xl" className="mt-[5px]">
                    Where do you schedule clients?
                </Heading>

                <WorkflowPage.Card className="mt-12">
                    <SelectList
                        selectionType="single"
                        items={locationListItems}
                        selectedItems={selectedLocation ? [selectedLocation] : []}
                        onSelectItems={async (items) => {
                            setSelectedLocation(items[0])
                            setIsEditingLocation(items[0].id === 'vendor-location')
                        }}
                    />
                </WorkflowPage.Card>
            </WorkflowPage>
            {isEditingLocation && selectedLocation?.id === 'vendor-location' && (
                <SetupLocationPage
                    title="My location"
                    onClose={() => {
                        if (!isValidLocation(user)) {
                            setSelectedLocation(undefined)
                        }
                        setIsEditingLocation(false)
                    }}
                    onComplete={(user) => {
                        setIsEditingLocation(false)

                        if (user && !isValidLocation(user)) {
                            setSelectedLocation(undefined)
                        }

                        if (user) {
                            setUser(user)
                        }
                    }}
                />
            )}
        </>
    )
}
